import { type PartialDeep } from 'type-fest';

import { type CanvasElementFrom, type PropsOf } from '@blaze-runtime/engine-domain';
type UploadFileElement = CanvasElementFrom<'uploadFile'>;
type UploadFileProps = PartialDeep<PropsOf<'uploadFile'>>;

function transformDropdownToUploadFile(source: CanvasElementFrom<'dropdown'>): UploadFileElement {
  const props: PartialDeep<PropsOf<'dropdown'>> = source.props || {};
  const newProps: UploadFileProps = {
    content: {
      button: {
        label: undefined, // no mapping
        icon: undefined, // no mapping
        iconPosition: undefined, // no mapping
        readonly: undefined, // no mapping
      },
      fileFormats: undefined, // no mapping
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: props.content?.title?.align,
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      general: {
        title: props.style?.general?.title,
      },
      button: {
        outlineWidth: undefined, // no mapping
        radius: props.style?.input?.radius,
      },
      upload: {
        label: undefined, // no mapping
        fill: undefined, // no mapping
        outline: undefined, // no mapping
      },
      uploaded: {
        label: props.style?.input?.color,
        fill: props.style?.input?.fill,
        outline: props.style?.input?.border,
      },
    },
  };

  // TODO: fix type
  return { props: newProps } as any;
}

function transformImageToUploadFile(source: CanvasElementFrom<'image'>): UploadFileElement {
  const props: PartialDeep<PropsOf<'image'>> = source.props || {};
  const newProps: UploadFileProps = {
    content: {
      button: {
        label: undefined, // no mapping
        icon: undefined, // no mapping
        iconPosition: undefined, // no mapping
        readonly: props.content?.options?.readonly,
      },
      fileFormats: undefined, // no mapping
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: props.content?.title?.align,
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      general: {
        title: props.style?.general?.title,
      },
      button: {
        outlineWidth: props.style?.outline?.width,
        radius: props.style?.outline?.radius,
      },
      upload: {
        label: undefined, // no mapping
        fill: undefined, // no mapping
        outline: undefined, // no mapping
      },
      uploaded: {
        label: undefined, // no mapping
        fill: undefined, // no mapping
        outline: props.style?.outline?.color,
      },
    },
  };

  // TODO: fix type
  return { props: newProps } as any;
}

function transformPdfViewerToUploadFile(source: CanvasElementFrom<'pdfViewer'>): UploadFileElement {
  const props: PartialDeep<PropsOf<'pdfViewer'>> = source.props || {};
  const newProps: UploadFileProps = {
    content: {
      button: {
        label: undefined, // no mapping
        icon: undefined, // no mapping
        iconPosition: undefined, // no mapping
        readonly: undefined, // no mapping
      },
      fileFormats: undefined, // no mapping
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
      },
    },
    style: {
      general: {
        title: props.style?.label?.color,
      },
      upload: {
        label: undefined, // no mapping
        fill: undefined, // no mapping
        outline: props.style?.border?.outline,
      },
      uploaded: {
        label: undefined, // no mapping
        fill: undefined, // no mapping
        outline: undefined, // no mapping
      },
    },
  };

  // TODO: fix type
  return { props: newProps } as any;
}

function transformRadioToUploadFile(source: CanvasElementFrom<'radio'>): UploadFileElement {
  const props: PartialDeep<PropsOf<'radio'>> = source.props || {};
  const newProps: UploadFileProps = {
    content: {
      button: {
        label: undefined, // no mapping
        icon: undefined, // no mapping
        iconPosition: undefined, // no mapping
        readonly: props.content?.radio?.readonly,
      },
      fileFormats: undefined, // no mapping
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: props.content?.title?.align,
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      general: {
        title: props.style?.general?.title,
      },
      button: {
        outlineWidth: undefined, // no mapping
        radius: undefined, // no mapping
      },
      upload: {
        label: undefined, // no mapping
        fill: undefined, // no mapping
        outline: undefined, // no mapping
      },
      uploaded: {
        label: props.style?.radio?.label,
        fill: props.style?.radio?.fill,
        outline: props.style?.radio?.border,
      },
    },
  };

  // TODO: fix type
  return { props: newProps } as any;
}

function transformTextInputToUploadFile(source: CanvasElementFrom<'textInput'>): UploadFileElement {
  const props: PartialDeep<PropsOf<'textInput'>> = source.props || {};
  const newProps: UploadFileProps = {
    content: {
      button: {
        label: undefined, // no mapping
        icon: undefined, // no mapping
        iconPosition: undefined, // no mapping
        readonly: props.content?.input?.readonly,
      },
      fileFormats: undefined, // no mapping
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: props.content?.title?.align?.horizontal,
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      general: {
        title: props.style?.general?.title,
      },
      button: {
        outlineWidth: undefined, // no mapping
        radius: props.style?.input?.radius,
      },
      upload: {
        label: undefined, // no mapping
        fill: undefined, // no mapping
        outline: undefined, // no mapping
      },
      uploaded: {
        label: props.style?.input?.value,
        fill: props.style?.input?.fill,
        outline: props.style?.input?.border,
      },
    },
  };

  // TODO: fix type
  return { props: newProps } as any;
}

export const transformToUploadFile = {
  transformDropdownToUploadFile,
  transformImageToUploadFile,
  transformPdfViewerToUploadFile,
  transformRadioToUploadFile,
  transformTextInputToUploadFile,
};
